import { useCallback } from 'react'

import { useQuery } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import { orgCompanyProfileQueryKeys } from '@/hooks/library/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { UUID } from '@/types/Id'
import type { EntityCompany } from '@/types/library/Entity'

export interface OrgCompanyProfileResponse {
  company: EntityCompany | null
}

interface UseOrgCompanyProfileArgs<T> {
  orgId: UUID | ''
  select?: (data: OrgCompanyProfileResponse) => T
}

interface UseOrgCompanyProfileOptions<T> extends UseOrgCompanyProfileArgs<T> {
  enabled?: boolean
}

const fetchOrgCompanyProfile = async (
  orgId: UUID | '',
  accessToken: string,
): Promise<OrgCompanyProfileResponse> => {
  if (!orgId) {
    throw new Error('orgId is required')
  }
  const response = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/entities/companies/ours`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    },
  )
  if (!response.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: response.statusText,
      cause: response,
    })
  }
  return (await response.json()) as OrgCompanyProfileResponse
}

export const useOrgCompanyProfile = <T = OrgCompanyProfileResponse>({
  orgId,
  select,
  enabled = true,
}: UseOrgCompanyProfileOptions<T>) => {
  const { accessToken } = useAuthInfoWithThrow()

  const selectFn = useCallback(
    (data: OrgCompanyProfileResponse) => {
      if (select) {
        return select(data)
      }
      return data as unknown as T
    },
    [select],
  )

  return useQuery({
    queryKey: orgCompanyProfileQueryKeys.organization({ orgId }),
    queryFn: () => fetchOrgCompanyProfile(orgId, accessToken),
    enabled: enabled && !!orgId && !!accessToken,
    staleTime: Infinity,
    select: selectFn,
  })
}
/**
 * Convenience hook to get just the company ID from the org profile
 */

const selectCompanyId = (data: OrgCompanyProfileResponse) => {
  return data.company?.id ?? null
}

interface UseOrgCompanyProfileIdArgs {
  orgId: UUID | ''
  enabled?: boolean
}

export const useOrgCompanyProfileId = ({
  orgId,
  enabled = true,
}: UseOrgCompanyProfileIdArgs) => {
  return useOrgCompanyProfile({
    orgId,
    select: selectCompanyId,
    enabled,
  })
}

interface UseHasOrgCompanyProfileArgs {
  orgId: UUID | ''
  enabled?: boolean
}

const selectHasProfile = (data: OrgCompanyProfileResponse) => {
  return data.company !== null
}

/**
 * Convenience hook to check if the org has a company profile
 */
export const useHasOrgCompanyProfile = ({
  orgId,
  enabled = true,
}: UseHasOrgCompanyProfileArgs) => {
  return useOrgCompanyProfile({
    orgId,
    select: selectHasProfile,
    enabled,
  })
}
